import * as THREE from 'three';
const { tan, cos, atan, acos, min, max, pow, PI, sqrt } = Math;
import clamp from '../utils/clamp';
import decompose from '../utils/decompose';
import { getCamera, getCameraWorldTransform, getRect } from '../../modules/player';
import { get, getWorldTransform } from '../../modules/scene';
import { merge } from '../../modules/sceneGraph';
import updateCameraLookAtTarget from '../utils/updateCameraLookAtTarget';
import getTargetWorldMatrix from '../utils/getTargetWorldMatrix';
import { virtualTargetId } from '../../modules/sceneGraph';

export function updateSphericalRotation(store, cameraId, cameraWorldMatrix, targetWorldMatrix, mouseDelta) {
  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const cameraData = get(store, { id:  cameraId, evalPlug: 'Camera' });

  const rect = getRect(store);

  const updateFns = { trackball: trackballUpdate, firstPerson: firstPersonUpdate };
  const updateFn = updateFns[playerData.orbitMode] || gyroscopeUpdate;
  updateFn(store, playerData, cameraId, cameraData, targetWorldMatrix, cameraWorldMatrix, rect, mouseDelta);
}

export default function orbit(store, translator) {
  return {
    label: 'Orbit',
    active: true,
    enabled: true,

    display: {
      playerTools: true,
    },
    options: {},

    tool: {
      drag: function (ev) {
        if (ev.which !== 1 || !(this.active || ev.metaKey)) return false;

        return {
          momentum: true,

          handle: function ({ deltaX, deltaY }) {
            const cameraId = getCamera(store);
            const cameraWorldMatrix = getCameraWorldTransform(store);
            const targetWorldMatrix = getTargetWorldMatrix(store, cameraId);

            updateSphericalRotation(store, cameraId, cameraWorldMatrix, targetWorldMatrix, new THREE.Vector2(deltaX, deltaY));
            store.dispatch(merge([cameraId, 'Transform', 0], decompose(cameraWorldMatrix)));

            if (!get(store, { id: cameraId, evalPlug: 'Transform' }).target) {
              store.dispatch(merge([virtualTargetId, 'Transform', 0], decompose(targetWorldMatrix)));
            }
          },
        };
      },
    },
  };
}

const cameraPosition = new THREE.Vector3();
const targetPosition = new THREE.Vector3();
const sphericalCamera = new THREE.Spherical();
const sphericalTarget = new THREE.Spherical();
const invTargetWorldMatrix = new THREE.Matrix4();
const invPivotMatrix = new THREE.Matrix4();

// changes cameraWorldMatrix by rotating by delta
function gyroscopeUpdate(store, playerData, cameraId, cameraData, targetWorldMatrix, cameraWorldMatrix, rect, { x, y }) {

  //to target space
  targetPosition.setFromMatrixPosition(targetWorldMatrix);
  cameraPosition.setFromMatrixPosition(cameraWorldMatrix).sub(targetPosition);

  let cameraSpherical = new THREE.Spherical().setFromVector3(cameraPosition);

  let azimuthalAngle = 2 * PI * (-x / rect.x);
  cameraSpherical.theta += azimuthalAngle;

  let polarAngle = 2 * PI * (-y / rect.y);
  cameraSpherical.phi += polarAngle;
  let maxPolarAngle;
  if (targetPosition.y / cameraPosition.length() <= 1) {
    maxPolarAngle = playerData.constraintCameraY ? acos(-targetPosition.y / cameraPosition.length()) : PI;
  } else {
    maxPolarAngle = PI;
  }

  cameraSpherical.phi = clamp(cameraSpherical.phi, 0, maxPolarAngle);

  cameraSpherical.makeSafe();
  cameraPosition.setFromSpherical(cameraSpherical).add(targetPosition);

  if (cameraData.radiusConstraint) {
    const { maxBoundingSphere, minBoundingSphere } = cameraData.radiusConstraint;
    const camToTarg = new THREE.Vector3().subVectors(targetPosition, cameraPosition);
    if (minBoundingSphere.containsPoint(cameraPosition)) {
      const backwardRay = new THREE.Ray(cameraPosition, camToTarg.multiplyScalar(-1));
      const newPosition = backwardRay.intersectSphere(minBoundingSphere).addScaledVector(camToTarg, __BOUNDING_BUFFER__);
      cameraPosition.copy(newPosition);
    } else if (!maxBoundingSphere.containsPoint(cameraPosition)) {
      const forwardRay = new THREE.Ray(cameraPosition, camToTarg);
      const newPosition = forwardRay.intersectSphere(maxBoundingSphere).addScaledVector(camToTarg, __BOUNDING_BUFFER__);
      cameraPosition.copy(newPosition);
    } else {}
  }

  cameraWorldMatrix.setPosition(cameraPosition);

  // update orientation
  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);
}

// changes cameraWorldMatrix by rotating by delta
function trackballUpdate(store, playerData, cameraId, cameraData, targetWorldMatrix, cameraWorldMatrix, rect, { x, y }) {
  const radius = min(rect.x, rect.y);

  const pivotMatrix = cameraData.pivotMatrix;
  const pivotPosition = new THREE.Vector3().setFromMatrixPosition(pivotMatrix);

  let relativeDelta = new THREE.Vector3().set(-2 * x / radius, 2 * y / radius, 0);
  if (relativeDelta.length() > 1) relativeDelta.normalize();
  if (relativeDelta.x === 0 && relativeDelta.y === 0) return;
  let delta = relativeDelta.length();

  //world to relative pivot space
  relativeDelta.transformDirection(cameraWorldMatrix).multiplyScalar(delta);
  cameraPosition.setFromMatrixPosition(cameraWorldMatrix).sub(pivotPosition);
  targetPosition.setFromMatrixPosition(targetWorldMatrix).sub(pivotPosition);
  //upPosition.transformDirection(invPivotMatrix);
  let camNorm = new THREE.Vector3().copy(cameraPosition).normalize();
  //create rotational Quaternion
  const scaleFactor = sqrt(1 - relativeDelta.lengthSq());
  relativeDelta.addScaledVector(camNorm, scaleFactor).normalize();

  let rotationalAxis = new THREE.Vector3().crossVectors(camNorm, relativeDelta).normalize();
  let rotationalAngle = camNorm.angleTo(relativeDelta);
  let rotationalQuaternion = new THREE.Quaternion().setFromAxisAngle(rotationalAxis, rotationalAngle);


  //pivot relative to world space
  cameraPosition.applyQuaternion(rotationalQuaternion).add(pivotPosition);
  targetPosition.applyQuaternion(rotationalQuaternion).add(pivotPosition);
  cameraData.upPosition.applyQuaternion(rotationalQuaternion);//.transformDirection(pivotMatrix);
  cameraData.offset.applyQuaternion(rotationalQuaternion);

  //bounding box constraining
  if (cameraData.radiusConstraint) {
    const { maxBoundingSphere, minBoundingSphere } = cameraData.radiusConstraint;
    const camToTarg = new THREE.Vector3().subVectors(targetPosition, cameraPosition);

    if (minBoundingSphere.containsPoint(cameraPosition)) {
      const backwardRay = new THREE.Ray(cameraPosition,
        new THREE.Vector3().copy(camToTarg).multiplyScalar(-1));

      const newPosition = backwardRay.intersectSphere(minBoundingSphere)
        .addScaledVector(camToTarg, -__BOUNDING_BUFFER__);

      cameraPosition.copy(newPosition);
    } else if (!maxBoundingSphere.containsPoint(cameraPosition)) {
      const forwardRay = new THREE.Ray(cameraPosition, camToTarg);

      const newPosition = forwardRay.intersectSphere(maxBoundingSphere)
        .addScaledVector(camToTarg, __BOUNDING_BUFFER__);

      cameraPosition.copy(newPosition);
    } else {}

    if (!minBoundingSphere.containsPoint(targetPosition)) {
      const thirdVector = new THREE.Vector3().crossVectors(camToTarg, cameraData.upPosition);
      const newPosition = minBoundingSphere.clampPoint(targetPosition);

      const posChange = new THREE.Vector3().subVectors(newPosition, targetPosition);
      newPosition.addScaledVector(posChange, __BOUNDING_BUFFER__);

      cameraData.upPosition.crossVectors(thirdVector,
        camToTarg.subVectors(newPosition, cameraPosition)).normalize();
      targetPosition.copy(newPosition);
    }
  }

  cameraWorldMatrix.setPosition(cameraPosition);
  targetWorldMatrix.setPosition(targetPosition);

  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);
}

function firstPersonUpdate(store, playerData, cameraId, cameraData, targetWorldMatrix, cameraWorldMatrix, rect, { x, y }) {
  cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
  targetPosition.setFromMatrixPosition(targetWorldMatrix).sub(cameraPosition);

  let targetSpherical = new THREE.Spherical().setFromVector3(targetPosition);

  let azimuthalAngle = 2 * PI * (-x / rect.x);
  targetSpherical.theta += azimuthalAngle;

  let polarAngle = 2 * PI * (y / rect.y);
  targetSpherical.phi += polarAngle;

  targetSpherical.makeSafe();
  targetPosition.setFromSpherical(targetSpherical).add(cameraPosition);

  targetWorldMatrix.setPosition(targetPosition);

  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);
}



// WEBPACK FOOTER //
// ./src/translators/tools/orbit.js